import React from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { Column } from "@material-table/core";
import { EditableTable } from "components/haven-design/editable-table";
import { ServerError } from "components/graphql-views";
import { useSnackbar } from "components/haven-design/snackbar";
import logger from "services/logger";
import { GET_PRICING_ALL, DELETE_PRICING } from "./queries";

type PricingData = {
  id: string;
  pricingTypeId: string;
  thresholdTypeId: string;

  /** GraphQL Decimal often arrives as a string at runtime */
  minimumPrice: number | string | null;
  unitThreshold: number;
  annualDiscountPercent: number;
  monthlyPricePerUnit: number | string | null;
  insertedAt: string;
  updatedAt: string;
  pricingType: {
    id: string;
    name: string;
  };
  thresholdType: {
    id: string;
    name: string;
  };
}

const finiteNumberOrNull = (value: unknown): number | null => {
  if (value === null || typeof value === "undefined") return null;
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? num : null;
};

const minimumPriceColumnDisplay = (rowData: PricingData): string => {
  const min = finiteNumberOrNull(rowData.minimumPrice);
  if (min === null) return "N/A";
  const name = rowData.pricingType?.name;
  let tag = "—";
  if (name === "Flat") tag = "flat plan";
  else if (name) tag = "minimum";
  return `$${min.toFixed(2)} (${tag})`;
};

const columns: Column<PricingData>[] = [
  {
    field: "pricingType.name",
    title: "Pricing Type",
    defaultSort: "asc",
    render: (rowData: PricingData) => rowData.pricingType?.name || "N/A",
  },
  {
    field: "thresholdType.name",
    title: "Threshold Type",
    render: (rowData: PricingData) =>
      rowData.thresholdType?.name || "N/A",
  },
  {
    field: "minimumPrice",
    title: "Flat price / monthly minimum ($)",
    type: "numeric",
    render: (rowData: PricingData) => minimumPriceColumnDisplay(rowData),
  },
  {
    field: "unitThreshold",
    title: "Unit Threshold",
    type: "numeric",
    render: (rowData: PricingData) =>
      (rowData.unitThreshold ? rowData.unitThreshold.toString() : "N/A"),
  },
  {
    field: "annualDiscountPercent",
    title: "Annual Discount %",
    type: "numeric",
    render: (rowData: PricingData) =>
      (rowData.annualDiscountPercent ? `${rowData.annualDiscountPercent}%` : "N/A"),
  },
  {
    field: "monthlyPricePerUnit",
    title: "Monthly Price Per Unit",
    type: "numeric",
    render: (rowData: PricingData) => {
      const m = finiteNumberOrNull(rowData.monthlyPricePerUnit);
      return m === null ? "N/A" : `$${m.toFixed(2)}`;
    },
  },
  {
    field: "updatedAt",
    title: "Last Updated",
    type: "datetime",
    render: (rowData: PricingData) =>
      new Date(rowData.updatedAt).toLocaleDateString(),
  },
];

type ResData = {
  pricingAll: PricingData[];
}

// eslint-disable-next-line max-lines-per-function
const PricingIndex: React.FC = () => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbar();
  const { loading, error, data } = useQuery<ResData>(GET_PRICING_ALL, {
    fetchPolicy: "no-cache",
  });

  const [deletePricing] = useMutation(
    DELETE_PRICING,
    {
      refetchQueries: ["pricingAll", "pricingAllForPlans", "pricingAllForSubscriptions"],
    },
  );

  const handleDelete = async (pricingId: string) => {
    try {
      await deletePricing({
        variables: { id: pricingId },
        refetchQueries: ["pricingAll", "pricingAllForPlans", "pricingAllForSubscriptions"],
      });
      openSnackbar({
        message: "Pricing configuration deleted successfully",
        status: "success",
      });
    } catch (err) {
      logger.sendError(err as Error);
      openSnackbar({
        message: "Failed to delete pricing configuration",
        status: "error",
      });
    }
  };

  if (error) return <ServerError desc={error.message} />;
  if (!data) return null;

  return (
    <EditableTable
      actions={[
        {
          icon: "add",
          tooltip: "Create Pricing Configuration",
          isFreeAction: true,
          onClick: () => navigate("/dash/admin/pricing/new"),
        },
      ]}
      title="Pricing Configurations"
      isLoading={loading}
      data={data.pricingAll}
      options={{
        pageSize: 20,
        pageSizeOptions: [10, 20, 50],
        search: true,
        sorting: true,
      }}
      columns={columns}
      editable={{
        onRowDelete: (oldData: PricingData) =>
          new Promise<void>((resolve, reject) => {
            handleDelete(oldData.id)
              .then(() => resolve())
              .catch(reject);
          }),
      }}
      onRowClick={(_, rowData) => rowData && navigate(`/dash/admin/pricing/${rowData.id}/edit`)}
    />
  );
};

export default PricingIndex;
